/**
 * planDeFormatText — ce que `format_text` met au plan de l'éditeur, et la
 * preuve qu'il exige avant d'écrire un passage (#1863, #1865).
 *
 * WHY un module à part de l'exécuteur : `formatTextExecutor.ts` parle à
 * l'éditeur ; ce module décide, sur ce que le fichier et l'aperçu disent, quoi
 * écrire, comment l'adresser, et quoi écarter. Il ne connaît l'éditeur que par
 * les réponses qu'on lui passe : ses décisions se testent sans navigateur.
 *
 * Les faits MESURÉS de l'éditeur dont il dépend — ce qu'un plan accepte, où
 * l'éditeur ne compte pas le texte comme le fichier — sont relevés dans
 * l'en-tête de `formatTextExecutor.ts`. Le plafond d'étapes par plan, lui, est
 * lu dans les typages de l'éditeur : sa source est sur {@link ETAPES_MAX_PAR_PLAN},
 * ici, et nulle part ailleurs.
 */

import type { CanvasFormatPlace, CanvasFormatSetAsideReason, CanvasTextFormatFilter, CanvasTextFormatSettingKey } from '@hector/shared-types';
import { CANVAS_TEXT_FORMAT_SETTING_KEYS } from '@hector/shared-types';
import { resolutionsParEtape } from './apercuDePlan';
import type { ParagrapheCible } from './cibleDeParagraphes';
import { adresseHorsDuCorps, direLeLieu, type AdresseHorsDuCorps } from './horsDuCorps';
import type { ActeVise } from '@hector/docx-forme';
import { passagesQuiSatisfont } from './passagesFiltres';
import {
  citer,
  decrireLesNonResolus,
  direLesPassagesVises,
  direLesTextes,
  porteeDuTexteVisePartout,
  type OrigineDeLaSelection,
  type RefusDeLEditeur,
  type ReleveDUnTexte,
} from './recuDEcritureDeForme';
import type { EtapeRefusee } from './repliDeLot';
import { ecritureSurLEtendue, type EcritureSurEtendue, type EtendueEcrite, type PlageDeTexte, type ReglagesDeTexte } from './verificationDeForme';

/**
 * Nombre maximal d'étapes par plan atomique.
 *
 * Source : `MAX_PLAN_STEPS = 200`, typages publics de SuperDoc 2.15.1
 * (`document-api/src/types/mutation-plan.types.d.ts`, lu le 2026-10-02). Une
 * cible plus large part en plusieurs plans, donc en plusieurs annulations : le
 * reçu en donne le nombre (`format.writes`).
 */
export const ETAPES_MAX_PAR_PLAN = 200;

/**
 * Nombre maximal d'aperçus successifs d'un plan filtré. Le premier désigne les
 * étapes que l'éditeur ne situe pas comme le fichier ; le second porte le plan
 * sans elles et doit être sans reproche. Valeur de jugement : un troisième tour
 * est une marge, au-delà le plan est refusé plutôt que rogné sans fin.
 */
export const TOURS_DE_PREUVE_MAX = 3;

/** Nombre maximal de paragraphes nommés dans un refus : assez pour que l'agent les écarte de sa cible. Valeur de jugement. */
export const PARAGRAPHES_CITES_MAX = 20;

/** Une étape de plan telle que l'éditeur la reçoit : de forme, ou de texte pour `rewrite_block` (`rewriteBlockExecutor.ts`). */
export interface EtapeDePlan {
  id: string;
  op: 'format.apply' | 'text.rewrite';
  where: Record<string, unknown>;
  args: Record<string, unknown>;
  /**
   * Présente pour un paragraphe d'en-tête, de pied ou de note : l'éditeur y
   * refuse les plans, le socle écrit donc l'étape seule, à cette adresse
   * (`horsDuCorps.ts`). Elle ne part jamais dans un plan.
   */
  horsDuCorps?: AdresseHorsDuCorps;
}

/** Une étape à écrire : un paragraphe — ou, quand l'écriture vise des passages, un passage de son texte — avec son `inline`. */
export interface EtapeAEcrire {
  paragraphe: ParagrapheCible;
  inline: EcritureSurEtendue['inline'];
  /** Présente quand l'écriture vise des passages : le passage visé. */
  plage?: PlageDeTexte;
  /**
   * Le passage visé est TOUT le texte du paragraphe, il s'écrit par
   * une étape de bloc, qui n'a besoin d'aucune position.
   */
  parBloc?: true;
}

/** Un passage que l'écriture vise — trouvé par un filtre, ou occurrence d'un texte visé dans tout l'acte —, avec le paragraphe lu qui le porte. */
export interface PassageVise {
  paragraphe: ParagrapheCible;
  plage: PlageDeTexte;
}

/** Un passage visé que l'écriture laisse tel quel, et pourquoi. */
export interface PassageEcarte extends PassageVise {
  raison: CanvasFormatSetAsideReason;
  /** La cause que donne l'éditeur, quand il refuse le passage et qu'elle seule l'explique. */
  detail?: string;
}

/** Les passages que l'écriture vise, et ce qu'un filtre n'a pas pu en dire. */
export interface SelectionDePassages {
  /** Les passages visés, dans l'ordre du document : ceux qui satisfont le filtre, ou toutes les occurrences sans filtre. */
  trouves: PassageVise[];
  /** Les paragraphes où le filtre ne s'évalue pas sur un passage au moins, avec leur nombre de passages. Vide sans filtre. */
  nonEvalues: Array<{ nodeId: string; passages: number }>;
  /** Les réglages du filtre qui ne se lisent pas sur ces passages, dans l'ordre du contrat. */
  reglagesNonLus: CanvasTextFormatSettingKey[];
}

/** Le texte d'un paragraphe lu — du corps ou d'une cellule — tel que le lecteur de forme le rend : celui que l'avocat voit, dans l'ordre. */
export const texteDuParagraphe = (paragraphe: { passages: ReadonlyArray<{ texte: string }> }): string => paragraphe.passages.map((passage) => passage.texte).join('');

/** Découpe une suite en tranches d'au plus `taille` éléments. */
export function enLots<T>(elements: readonly T[], taille: number): T[][] {
  const lots: T[][] = [];
  for (let debut = 0; debut < elements.length; debut += taille) lots.push(elements.slice(debut, debut + taille));
  return lots;
}

/** Une étendue où chercher : un paragraphe entier, ou la part de son texte que `dans` borne. */
interface EtendueAFiltrer {
  paragraphe: ParagrapheCible;
  dans?: { debut: number; fin: number };
}

/** Les passages de ces étendues qui satisfont le filtre, dans leur ordre ; un paragraphe non évalué n'est nommé qu'une fois. */
function filtrerLesEtendues(etendues: readonly EtendueAFiltrer[], filtre: CanvasTextFormatFilter): SelectionDePassages {
  const selection: SelectionDePassages = { trouves: [], nonEvalues: [], reglagesNonLus: [] };
  const nonLus = new Set<CanvasTextFormatSettingKey>();
  for (const { paragraphe, dans } of etendues) {
    const lus = passagesQuiSatisfont(paragraphe, filtre, dans);
    for (const plage of lus.plages) selection.trouves.push({ paragraphe, plage });
    if (lus.nonEvalues > 0) {
      const dernier = selection.nonEvalues.at(-1);
      if (dernier?.nodeId === paragraphe.nodeId) dernier.passages += lus.nonEvalues;
      else selection.nonEvalues.push({ nodeId: paragraphe.nodeId, passages: lus.nonEvalues });
    }
    for (const reglage of lus.reglagesNonLus) nonLus.add(reglage);
  }
  selection.reglagesNonLus = CANVAS_TEXT_FORMAT_SETTING_KEYS.filter((reglage) => nonLus.has(reglage));
  return selection;
}

/**
 * Les passages de la cible qui satisfont le filtre, dans l'ordre du document.
 *
 * @param passage Le passage d'une cible « passage », déjà prouvé unique dans
 *   son paragraphe : il restreint la recherche à son étendue.
 */
export function passagesDuFiltre(paragraphes: readonly ParagrapheCible[], passage: string | undefined, filtre: CanvasTextFormatFilter): SelectionDePassages {
  return filtrerLesEtendues(paragraphes.map((paragraphe): EtendueAFiltrer => {
    if (passage === undefined) return { paragraphe };
    const debut = texteDuParagraphe(paragraphe).indexOf(passage);
    return { paragraphe, dans: { debut, fin: debut + passage.length } };
  }), filtre);
}

/** Ce qu'un ou plusieurs textes exacts désignent dans tout l'acte (cible « texte » sans paragraphe, #1947). */
export interface OccurrencesDansLActe {
  /** Les occurrences des paragraphes du corps, tous textes réunis, dans l'ordre du document, sans chevauchement : celles que l'écriture vise. */
  trouvees: PassageVise[];
  /** Les paragraphes de cellule de tableau qui portent un des textes, par leur identifiant (`null` : le fichier ne leur en donne pas). */
  cellules: Array<string | null>;
  /** Nombre de paragraphes du corps qui portent un des textes sans identifiant au fichier : aucune écriture ne les adresse. */
  sansIdentifiant: number;
  /** Nombre de paragraphes qui portent un des textes et dont l'identifiant est en double dans le lieu (`acteVise`, `horsDuCorps.ts`) : aucune écriture ne les adresse non plus. */
  enDouble: number;
  /** Nombre d'occurrences que portent ces cellules et ces paragraphes hors de portée. */
  horsDePortee: number;
  /** Un relevé par texte DISTINCT, dans l'ordre donné — dont le nombre de fois où il n'apparaît que dans un mot ou un nombre plus long : pas des occurrences, mais ce qu'un refus doit pouvoir dire. */
  parTexte: ReleveDUnTexte[];
}

/** Une lettre ou un chiffre, de tout alphabet : ce qui, accolé à une occurrence, en fait un morceau d'un mot plus long. */
const FINIT_PAR_UN_CARACTERE_DE_MOT = /[\p{L}\p{N}]$/u;
const COMMENCE_PAR_UN_CARACTERE_DE_MOT = /^[\p{L}\p{N}]/u;

/**
 * Un nombre écrit à la française se lit en groupes de trois chiffres séparés
 * par une espace (simple, insécable, fine) ou un point, puis une décimale.
 * Valeur de jugement : ces trois motifs suffisent à ne pas couper « 11 500 » en
 * « 500 », ni « 1 500,50 » ou « 1 500 000 » en « 1 500 », sans écarter un nombre
 * simplement voisin d'un autre (« 2024 1 500 euros », « 12, 13 »).
 */
const COMMENCE_PAR_UN_GROUPE_DE_MILLIERS = /^\p{N}{3}(?!\p{N})/u;
const FINIT_PAR_UN_CHIFFRE_ET_UN_SEPARATEUR = /\p{N}[ \u00a0\u202f.]$/u;
const FINIT_PAR_UN_CHIFFRE = /\p{N}$/u;
const COMMENCE_PAR_LA_SUITE_DU_NOMBRE = /^(?:[,.]\p{N}|[ \u00a0\u202f]\p{N}{3}(?!\p{N}))/u;

/**
 * Vrai si `[debut, fin[` est un MOT ENTIER de `dans`. La frontière n'est exigée
 * que du côté où le texte visé finit lui-même par une lettre ou un chiffre : un
 * signe en bord de texte (« n° », « % », « l' ») est déjà une frontière, et
 * l'exiger ferait refuser un texte copié juste. Un nombre ne se coupe ni dans
 * ses milliers ni avant sa décimale. Le début et la fin du paragraphe valent
 * frontière. Deux unités sont lues de chaque côté : un caractère hors du plan
 * de base en occupe deux.
 *
 * Exportée pour le mode partout de `replace_text` (`remplacementPartout.ts`),
 * qui cherche ses occurrences sous la même règle : une seule définition du mot
 * entier pour ce qui met en forme et pour ce qui remplace.
 */
export const estUnMotEntier = (dans: string, debut: number, fin: number): boolean => {
  const texte = dans.slice(debut, fin);
  const avant = dans.slice(0, debut);
  const apres = dans.slice(fin);
  if (COMMENCE_PAR_UN_CARACTERE_DE_MOT.test(texte) && FINIT_PAR_UN_CARACTERE_DE_MOT.test(avant.slice(-2))) return false;
  if (FINIT_PAR_UN_CARACTERE_DE_MOT.test(texte) && COMMENCE_PAR_UN_CARACTERE_DE_MOT.test(apres.slice(0, 2))) return false;
  if (COMMENCE_PAR_UN_GROUPE_DE_MILLIERS.test(texte) && FINIT_PAR_UN_CHIFFRE_ET_UN_SEPARATEUR.test(avant.slice(-3))) return false;
  if (FINIT_PAR_UN_CHIFFRE.test(texte) && COMMENCE_PAR_LA_SUITE_DU_NOMBRE.test(apres.slice(0, 6))) return false;
  return true;
};

/**
 * Les occurrences de `texte` en MOT ENTIER dans un texte de paragraphe, de
 * gauche à droite, SANS chevauchement : deux étapes d'un plan sur des plages
 * qui se chevauchent le font refuser (`TARGET_CONFLICT`, en-tête de
 * `formatTextExecutor.ts`). Une occurrence accolée à une lettre ou à un chiffre
 * n'en est pas une, et n'empêche pas d'en trouver une qui la chevauche.
 */
function plagesDuTexte(dans: string, texte: string): { plages: PlageDeTexte[]; accolees: number } {
  const plages: PlageDeTexte[] = [];
  let accolees = 0;
  for (let debut = dans.indexOf(texte); debut >= 0;) {
    const fin = debut + texte.length;
    const entier = estUnMotEntier(dans, debut, fin);
    if (entier) plages.push({ debut, fin, texte });
    else accolees += 1;
    debut = dans.indexOf(texte, entier ? fin : debut + 1);
  }
  return { plages, accolees };
}

/**
 * Les occurrences de plusieurs textes dans un texte de paragraphe, réunies en
 * plages DISJOINTES, de gauche à droite : là où deux occurrences se
 * chevauchent, la plus longue reste et l'autre est écartée — à longueur égale,
 * la première. Deux étapes d'un plan sur des plages qui se chevauchent le font
 * refuser (`TARGET_CONFLICT`, en-tête de `formatTextExecutor.ts`) ; deux plages
 * qui se touchent sont acceptées.
 *
 * @param textes Des textes distincts.
 * @returns `ecartees` : une entrée par occurrence écartée, `contenue` quand elle tient en entier dans une plage gardée.
 */
function plagesDesTextes(dans: string, textes: readonly string[]): { plages: PlageDeTexte[]; ecartees: Array<{ texte: string; contenue: boolean }>; accolees: number[] } {
  const releves = textes.map((texte) => plagesDuTexte(dans, texte));
  const candidates = releves.flatMap((releve) => releve.plages).sort((a, b) => (b.fin - b.debut) - (a.fin - a.debut) || a.debut - b.debut);
  const plages: PlageDeTexte[] = [];
  const ecartees: Array<{ texte: string; contenue: boolean }> = [];
  for (const candidate of candidates) {
    const rivales = plages.filter((gardee) => gardee.debut < candidate.fin && candidate.debut < gardee.fin);
    if (rivales.length === 0) plages.push(candidate);
    else ecartees.push({ texte: candidate.texte, contenue: rivales.some((gardee) => gardee.debut <= candidate.debut && candidate.fin <= gardee.fin) });
  }
  return { plages: plages.sort((a, b) => a.debut - b.debut), ecartees, accolees: releves.map((releve) => releve.accolees) };
}

/**
 * Toutes les occurrences de textes exacts dans l'acte lu : sensible à la casse,
 * dans le texte que l'avocat voit, à l'intérieur d'UN paragraphe (un texte à
 * cheval sur deux paragraphes n'est pas une occurrence), en MOT ENTIER. Les
 * occurrences de tous les textes forment UNE liste, dans l'ordre du document,
 * sans chevauchement ({@link plagesDesTextes}) : la suite de l'écriture ne sait
 * pas de combien de textes elle vient.
 *
 * WHY le mot entier : sur tout l'acte, personne ne relit chaque occurrence
 * avant l'écriture. « 1 500 euros » trouvé dans « 11 500 euros » serait une
 * forme posée au mauvais endroit, sous un reçu `ok`. ⚠️ La cible AVEC
 * paragraphe (`resoudreLaCible`, `formatTextExecutor.ts`) garde sa lecture
 * d'origine — une suite de caractères, trouvée une fois dans le paragraphe que
 * l'agent a nommé : l'écart entre les deux est une décision à confirmer par le
 * porteur.
 *
 * Portée : les paragraphes de l'acte passé — le corps avec ses cellules de
 * tableau (`corpsAvecSesCellules`), où le texte d'une note, d'un en-tête ou
 * d'un pied n'est ni cherché ni compté ; ou les paragraphes d'un lieu (`acteVise`,
 * `horsDuCorps.ts`), et lui seul, dont les cellules de tableau sont cherchées
 * et comptées hors de portée.
 *
 * Ce que les verbes de forme n'atteignent pas (`cibleDeParagraphes.ts`) n'est
 * pas rendu à écrire : compté, et nommé quand le fichier le nomme.
 *
 * @param textes Les textes visés, un seul compris ; un texte donné deux fois compte pour un.
 */
export function occurrencesDansLActe(acte: ActeVise, textes: readonly string[]): OccurrencesDansLActe {
  const distincts = [...new Set(textes)];
  const parTexte = new Map(distincts.map((texte): [string, ReleveDUnTexte] => [texte, { texte, visees: 0, dansUnPlusLong: 0, chevauchantUnPlusLong: 0, horsDePortee: 0, accolees: 0 }]));
  const releve: OccurrencesDansLActe = { trouvees: [], cellules: [], sansIdentifiant: 0, enDouble: 0, horsDePortee: 0, parTexte: [...parTexte.values()] };
  // Ce qu'un paragraphe porte, compté au nom de chaque texte ; rend ses plages, que l'appelant range selon la portée du paragraphe.
  const relever = (lu: { passages: ReadonlyArray<{ texte: string }> }, sort: 'visees' | 'horsDePortee'): PlageDeTexte[] => {
    const { plages, ecartees, accolees } = plagesDesTextes(texteDuParagraphe(lu), distincts);
    accolees.forEach((nombre, rang) => { releve.parTexte[rang]!.accolees += nombre; });
    for (const { texte, contenue } of ecartees) parTexte.get(texte)![contenue ? 'dansUnPlusLong' : 'chevauchantUnPlusLong'] += 1;
    for (const { texte } of plages) parTexte.get(texte)![sort] += 1;
    return plages;
  };
  acte.paragraphes.forEach((lu, rang) => {
    const plages = relever(lu, lu.nodeId === null ? 'horsDePortee' : 'visees');
    if (plages.length === 0) return;
    if (lu.nodeId === null) {
      releve[lu.identifiantEnDouble === undefined ? 'sansIdentifiant' : 'enDouble'] += 1;
      releve.horsDePortee += plages.length;
      return;
    }
    const paragraphe: ParagrapheCible = { ...lu, nodeId: lu.nodeId, rang };
    for (const plage of plages) releve.trouvees.push({ paragraphe, plage });
  });
  for (const cellule of acte.cellulesNonVisees) {
    const plages = relever(cellule, 'horsDePortee');
    if (plages.length === 0) continue;
    releve.cellules.push(cellule.nodeId);
    releve.horsDePortee += plages.length;
  }
  return releve;
}

/**
 * Le refus à rendre quand AUCUN des textes visés dans tout l'acte n'a
 * d'occurrence à écrire : rien n'est écrit. Il nomme chaque texte, et ce qui
 * explique son absence — là seulement dans un mot plus long, ou hors de portée.
 * Un texte seul garde son refus d'origine.
 *
 * `reessayer` : les textes ne sont nulle part, l'agent peut les corriger et
 * renvoyer. Absent quand renvoyer finirait de la même façon.
 *
 * @param lieu L'en-tête, le pied ou les notes où les textes ont été cherchés ; absent : le corps.
 */
export function refusSansOccurrence(releve: OccurrencesDansLActe, lieu?: CanvasFormatPlace): { code: 'FORMAT_PASSAGE_NOT_FOUND'; message: string; reessayer?: true } {
  const { parTexte, cellules, sansIdentifiant, enDouble, horsDePortee } = releve;
  const accolees = parTexte.reduce((somme, texte) => somme + texte.accolees, 0);
  const [seul, ...autres] = parTexte;
  const unSeul = seul !== undefined && autres.length === 0;
  const cellulesNommees = cellules.filter((nodeId): nodeId is string => nodeId !== null);
  const vises = unSeul
    ? `${citer(seul.texte)} (case-sensitive, as a whole word, plain text as displayed, inside one paragraph)`
    : `the ${parTexte.length} texts given: ${direLesTextes(parTexte)} (case-sensitive, as whole words, plain text as displayed, inside one paragraph)`;
  // Dans un lieu, une cible ne nomme pas de paragraphe : ni `{kind:"ids"}`, ni `nodeId` avec un texte.
  const nommerLeParagraphe = lieu === undefined ? 'name its paragraph: {kind:"text", nodeId, text}' : `format the whole place with {kind:"all", in} — ${direLeLieu(lieu)} takes no paragraph id`;
  const accoleesDites = accolees === 0 ? '' : unSeul
    ? `The text appears ${accolees} time(s) only inside a longer word or number (for instance "500" inside "11 500"), which is not an occurrence. To format such a place, ${nommerLeParagraphe}. `
    : `To format a place where a text sits inside a longer word or number, ${nommerLeParagraphe}. `;
  const copierLeTexte = lieu === undefined
    ? `Copy ${unSeul ? 'the' : 'each'} text exactly as the document reads, or target paragraphs with {kind:"ids"}.`
    : `Copy ${unSeul ? 'the' : 'each'} text exactly as ${direLeLieu(lieu)} reads (canvas_inspect with formatOf {kind:"all"} and the same "in" shows it), or format the whole place with {kind:"all", in}.`;
  return {
    code: 'FORMAT_PASSAGE_NOT_FOUND',
    ...(horsDePortee === 0 && accolees === 0 ? { reessayer: true as const } : {}),
    message: `format_text was refused before any write: ${direLeLieu(lieu)} contains 0 occurrence(s) of ${vises}. Nothing was written. ${porteeDuTexteVisePartout(lieu)} `
      + accoleesDites
      + (horsDePortee === 0
        ? (accolees === 0 ? copierLeTexte : 'Resending the same target would end the same way.')
        : `${horsDePortee} occurrence(s) are out of reach, in ${cellules.length} table-cell paragraph(s) and ${sansIdentifiant} paragraph(s) without an identifier`
          + (enDouble === 0 ? '' : ` and ${enDouble} paragraph(s) whose identifier another paragraph also carries`)
          + `${cellulesNommees.length === 0 ? '' : `: ${decrireLesNonResolus(cellulesNommees.slice(0, PARAGRAPHES_CITES_MAX).map((nodeId) => ({ nodeId, reason: 'table_cell' as const })))}`}. `
          + 'Resending would end the same way; tell the lawyer this text keeps its form.'),
  };
}

/**
 * Ce que l'écriture vise au grain du PASSAGE, d'où qu'il vienne : les passages
 * qu'un filtre trouve dans la cible, les occurrences d'un texte visé dans tout
 * l'acte, ou la part de ces occurrences que le filtre retient. `undefined` :
 * l'écriture vise des paragraphes (ou l'unique passage d'un paragraphe).
 *
 * La suite — plan, aperçu, repli, relecture, reçu — consomme cette liste sans
 * savoir de laquelle de ces trois façons elle a été bâtie.
 */
export function passagesVises(
  cible: { paragraphes: readonly ParagrapheCible[]; passage?: string; occurrences?: readonly PassageVise[] },
  filtre: CanvasTextFormatFilter | undefined,
): SelectionDePassages | undefined {
  const { occurrences } = cible;
  if (occurrences === undefined) return filtre === undefined ? undefined : passagesDuFiltre(cible.paragraphes, cible.passage, filtre);
  if (filtre === undefined) return { trouves: [...occurrences], nonEvalues: [], reglagesNonLus: [] };
  return filtrerLesEtendues(occurrences.map(({ paragraphe, plage }) => ({ paragraphe, dans: plage })), filtre);
}

/**
 * Ce qui est à écrire : la cible — ses paragraphes, ou les passages trouvés par
 * le filtre — moins ce où l'écriture ne changerait rien. L'éditeur refuserait
 * le lot entier pour une étape sans effet (voir l'en-tête de l'exécuteur).
 *
 * @param passage Le passage d'une cible « passage », sans filtre.
 */
export function etapesAEcrire(
  acte: ActeVise,
  paragraphes: readonly ParagrapheCible[],
  passage: string | undefined,
  reglages: ReglagesDeTexte,
  trouves: readonly PassageVise[] | undefined,
): EtapeAEcrire[] {
  const aViser: ReadonlyArray<{ paragraphe: ParagrapheCible; plage?: PlageDeTexte }> = trouves ?? paragraphes.map((paragraphe) => ({ paragraphe }));
  return aViser.flatMap(({ paragraphe, plage }): EtapeAEcrire[] => {
    const etendue: EtendueEcrite = plage === undefined
      ? { nodeId: paragraphe.nodeId, ...(passage === undefined ? {} : { passage }) }
      : { nodeId: paragraphe.nodeId, plage };
    const ecriture = ecritureSurLEtendue(acte, etendue, reglages);
    if (ecriture.sansEffet) return [];
    const toutLeParagraphe = plage !== undefined && plage.debut === 0 && plage.fin === texteDuParagraphe(paragraphe).length;
    return [{ paragraphe, inline: ecriture.inline, ...(plage === undefined ? {} : { plage }), ...(toutLeParagraphe ? { parBloc: true as const } : {}) }];
  });
}

/**
 * Le motif qui adresse une plage par sa seule POSITION dans le texte de son
 * paragraphe : `debut` caractères quelconques depuis le début, puis la longueur
 * de la plage. Le texte n'y figure pas : c'est la comparaison du texte entier
 * du paragraphe, puis l'aperçu, qui prouvent qu'il est celui du fichier.
 */
const motifDePosition = ({ debut, fin }: PlageDeTexte): string => `(?<=^[\\s\\S]{${debut}})[\\s\\S]{${fin - debut}}`;

/** L'étape de bloc d'un paragraphe : tout son texte, sans position. */
const etapeDeBloc = (id: string, paragraphe: ParagrapheCible, inline: EtapeAEcrire['inline']): EtapeDePlan => (
  // `nodeType` est exigé par la forme de l'étape mais ne décide pas du bloc
  // visé : l'identifiant suffit (mesuré, voir l'en-tête de l'exécuteur).
  { id, op: 'format.apply', where: { by: 'block', nodeType: 'paragraph', nodeId: paragraphe.nodeId }, args: { inline, scope: 'block' }, ...adresseHorsDuCorps(paragraphe) }
);

/**
 * Les étapes du plan, dans l'ordre de `aEcrire` : l'identifiant d'une étape est
 * `p` suivi de son rang dans cette suite. Exportée pour ses tests seulement :
 * le code de production passe par {@link prouverLePlan}.
 *
 * @param passage Le passage d'une cible « passage », sans filtre : adressé par son texte.
 */
export function etapesDuPlan(aEcrire: readonly EtapeAEcrire[], passage: string | undefined): EtapeDePlan[] {
  return aEcrire.map(({ paragraphe, inline, plage, parBloc }, rang): EtapeDePlan => {
    const id = `p${rang}`;
    const dans = { kind: 'block', nodeType: 'paragraph', nodeId: paragraphe.nodeId };
    if (plage !== undefined && parBloc !== true) {
      return {
        id,
        op: 'format.apply',
        where: { by: 'select', select: { type: 'text', mode: 'regex', pattern: motifDePosition(plage), caseSensitive: true }, within: dans, require: 'exactlyOne' },
        args: { inline },
        ...adresseHorsDuCorps(paragraphe, plage),
      };
    }
    if (plage !== undefined || passage === undefined) return etapeDeBloc(id, paragraphe, inline);
    return {
      id,
      op: 'format.apply',
      where: { by: 'select', select: { type: 'text', pattern: passage, caseSensitive: true }, within: dans, require: 'exactlyOne' },
      args: { inline },
    };
  });
}

/**
 * L'étape de l'aperçu de LECTURE qui porte le paragraphe de rang `rang` dans
 * son lot : même forme d'identifiant que les étapes du plan (une lettre, puis
 * le rang), pour que le refus d'un aperçu nomme le paragraphe de la même façon.
 */
const idDeLecture = (rang: number): string => `t${rang}`;

/**
 * Les paragraphes dont il faut lire le texte dans l'éditeur avant d'y adresser
 * un passage par position : ceux qui portent un passage à écrire qui n'est pas
 * tout leur texte. Dans l'ordre du document, sans doublon.
 */
function paragraphesALire(candidats: readonly EtapeAEcrire[]): ParagrapheCible[] {
  return [...new Set(candidats.filter((etape) => etape.plage !== undefined && etape.parBloc !== true).map((etape) => etape.paragraphe))];
}

/**
 * Le plan de LECTURE : une étape de bloc par paragraphe, à passer à l'aperçu
 * seul, jamais à l'application. L'aperçu d'une étape de bloc rend le texte
 * entier du paragraphe tel que l'éditeur le compte (`mesure-format-text-only-where`, Q15).
 */
function etapesDeLecture(paragraphes: readonly ParagrapheCible[], inline: EtapeAEcrire['inline']): EtapeDePlan[] {
  return paragraphes.map((paragraphe, rang) => etapeDeBloc(idDeLecture(rang), paragraphe, inline));
}

/**
 * Le texte de chaque paragraphe lu, tel que l'aperçu de lecture le rend.
 * `null` : l'aperçu n'a pas rendu, pour ce paragraphe, UNE plage qui parte du
 * début de son texte.
 *
 * @param rendu La réponse brute de l'aperçu du plan de {@link etapesDeLecture}.
 */
function textesLus(rendu: unknown, paragraphes: readonly ParagrapheCible[]): Map<string, string | null> {
  const resolues = resolutionsParEtape(rendu);
  return new Map(paragraphes.map((paragraphe, rang) => {
    const [lue, ...autres] = resolues.get(idDeLecture(rang)) ?? [];
    const lisible = lue !== undefined && autres.length === 0 && lue.blockId === paragraphe.nodeId && lue.debut === 0;
    return [paragraphe.nodeId, lisible ? lue.texte : null];
  }));
}

/**
 * Ce qu'un aperçu rend : un refus à dire tel quel, ou les étapes que l'éditeur
 * accepte (`gardees`, avec `rendu`, la réponse de leur aperçu valide) et celles
 * qu'il refuse, chacune avec sa cause (`ecritureDeForme.ts`, `apercevoir`).
 */
export type ApercuDeLot<Refus> = { refus: Refus } | { rendu: unknown; gardees: readonly EtapeDePlan[]; refusees: readonly EtapeRefusee[] };

/** Une étape à écrire que l'éditeur refuse, avec ce qu'il en dit : elle est sautée, le reste du plan est écrit. */
export interface EtapeSautee {
  etape: EtapeAEcrire;
  refus: RefusDeLEditeur;
}

/** L'élément que désigne l'identifiant d'une étape de plan : `p`, puis son rang dans `etapes`. */
export const etapeDuPlan = <Etape>(etapes: readonly Etape[], stepId: string): Etape | undefined => etapes[Number(stepId.slice(1))];

/**
 * Lit dans l'éditeur le texte de chaque paragraphe où un passage va s'adresser
 * par position : un aperçu de LECTURE par tranche de {@link ETAPES_MAX_PAR_PLAN}
 * paragraphes, jamais appliqué.
 *
 * WHY un refus et pas un écart quand l'aperçu échoue : un aperçu que l'éditeur
 * jette, refuse ou rend illisible ne dit rien d'aucun paragraphe, et sa cause
 * peut être passagère. Écarter tous les passages du lot dirait à l'agent de ne
 * pas renvoyer l'appel, à tort. `null` reste pour un aperçu VALIDE qui ne rend
 * pas le texte d'un paragraphe donné — ou dont l'éditeur refuse l'étape de
 * lecture : son texte n'est pas prouvé non plus.
 *
 * @param apercevoir Aperçoit UN lot ; `aLire` porte, au rang de chaque étape,
 *   le paragraphe qu'elle lit.
 */
export async function lireLesTextes<Refus>(
  candidats: readonly EtapeAEcrire[],
  apercevoir: (steps: EtapeDePlan[], aLire: readonly EtapeAEcrire[]) => Promise<ApercuDeLot<Refus>>,
): Promise<{ textes: Map<string, string | null> } | { refus: Refus }> {
  const textes = new Map<string, string | null>();
  const [premier] = candidats;
  if (premier === undefined) return { textes };
  // L'aperçu d'une étape de bloc rend le texte quel que soit son `inline` : celui du premier candidat suffit.
  const { inline } = premier;
  for (const lot of enLots(paragraphesALire(candidats), ETAPES_MAX_PAR_PLAN)) {
    const apercu = await apercevoir(etapesDeLecture(lot, inline), lot.map((paragraphe) => ({ paragraphe, inline })));
    if ('refus' in apercu) return apercu;
    for (const [nodeId, texte] of textesLus(apercu.rendu, lot)) textes.set(nodeId, texte);
  }
  return { textes };
}

/**
 * Sépare les étapes qu'on peut adresser de celles qu'on écarte, d'après le
 * texte que l'éditeur donne de chaque paragraphe.
 *
 * WHY le texte ENTIER : une position prise au fichier ne désigne le même texte
 * dans l'éditeur que si les deux comptent les mêmes caractères depuis le début
 * du paragraphe. Comparer le seul texte du passage ne le prouve pas — un
 * passage uniforme (« ____ ») décalé d'un caractère porte encore le même texte.
 * Un paragraphe dont le texte diffère n'est donc écrit nulle part par position.
 * Un passage qui est tout le texte de son paragraphe s'écrit par bloc : il
 * n'est jamais écarté ici.
 */
export function situerLesPassages(
  candidats: readonly EtapeAEcrire[],
  textes: ReadonlyMap<string, string | null>,
): { aEcrire: EtapeAEcrire[]; ecartes: PassageEcarte[] } {
  const aEcrire: EtapeAEcrire[] = [];
  const ecartes: PassageEcarte[] = [];
  for (const etape of candidats) {
    const { paragraphe, plage } = etape;
    if (plage === undefined || etape.parBloc === true) {
      aEcrire.push(etape);
      continue;
    }
    const lu = textes.get(paragraphe.nodeId);
    if (lu === texteDuParagraphe(paragraphe)) aEcrire.push(etape);
    else ecartes.push({ paragraphe, plage, raison: lu === null || lu === undefined ? 'paragraph_text_unreadable' : 'paragraph_text_differs' });
  }
  return { aEcrire, ecartes };
}

/**
 * Les étapes par position d'un lot que son aperçu ne prouve pas : l'éditeur n'y
 * vise pas, d'UNE plage, le paragraphe, la position et le texte du fichier.
 *
 * @param rendu La réponse brute d'un aperçu VALIDE du lot.
 * @param steps Les étapes du lot, dont l'identifiant donne le rang dans `aEcrire`.
 */
function etapesNonProuvees(rendu: unknown, steps: readonly EtapeDePlan[], aEcrire: readonly EtapeAEcrire[]): EtapeAEcrire[] {
  const resolues = resolutionsParEtape(rendu);
  return steps.flatMap(({ id }): EtapeAEcrire[] => {
    const etape = etapeDuPlan(aEcrire, id);
    if (etape === undefined || etape.plage === undefined || etape.parBloc === true) return [];
    const [visee, ...autres] = resolues.get(id) ?? [];
    const prouvee = visee !== undefined && autres.length === 0 && visee.blockId === etape.paragraphe.nodeId
      && visee.debut === etape.plage.debut && visee.fin === etape.plage.fin && visee.texte === etape.plage.texte;
    return prouvee ? [] : [etape];
  });
}

/**
 * Aperçoit tous les lots jusqu'à ce que chaque étape par position soit prouvée.
 * Les étapes non prouvées sont écartées et le plan aperçu de nouveau sans
 * elles, {@link TOURS_DE_PREUVE_MAX} fois au plus. Les étapes que l'éditeur
 * REFUSE à l'aperçu sont retirées du plan et rendues avec leur cause
 * (`sautees`) : le reste est écrit.
 *
 * @param apercevoir Aperçoit UN lot ; `aEcrire` est la suite dont les
 *   identifiants d'étape donnent le rang. Un aperçu que l'éditeur jette, rend
 *   illisible, ou refuse sans nommer d'étape est un `refus` : il arrête tout,
 *   rien n'est écarté pour lui.
 * @returns Le plan prouvé — `lots`, dont chaque identifiant d'étape désigne
 *   son étape dans `etapes` ({@link etapeDuPlan}) ; `aEcrire`, les étapes de ces
 *   lots dans l'ordre — et ce qui en a été écarté ou sauté ; le refus d'un
 *   aperçu ; ou `nonProuvees` si le dernier tour laisse encore des étapes non
 *   prouvées.
 */
export async function prouverLePlan<Refus>(
  candidats: readonly EtapeAEcrire[],
  passage: string | undefined,
  apercevoir: (steps: EtapeDePlan[], aEcrire: readonly EtapeAEcrire[]) => Promise<ApercuDeLot<Refus>>,
): Promise<
  | { aEcrire: EtapeAEcrire[]; etapes: EtapeAEcrire[]; lots: EtapeDePlan[][]; ecartes: PassageEcarte[]; sautees: EtapeSautee[] }
  | { refus: Refus }
  | { nonProuvees: EtapeAEcrire[] }
> {
  let etapes = [...candidats];
  const ecartes: PassageEcarte[] = [];
  const sautees: EtapeSautee[] = [];
  for (let tour = 1; ; tour += 1) {
    const lots: EtapeDePlan[][] = [];
    const nonProuvees = new Set<EtapeAEcrire>();
    const refusees = new Map<EtapeAEcrire, RefusDeLEditeur>();
    for (const steps of enLots(etapesDuPlan(etapes, passage), ETAPES_MAX_PAR_PLAN)) {
      const apercu = await apercevoir(steps, etapes);
      if ('refus' in apercu) return apercu;
      for (const { stepId, refus } of apercu.refusees) {
        const etape = etapeDuPlan(etapes, stepId);
        if (etape !== undefined) refusees.set(etape, refus);
      }
      for (const etape of etapesNonProuvees(apercu.rendu, apercu.gardees, etapes)) nonProuvees.add(etape);
      if (apercu.gardees.length > 0) lots.push([...apercu.gardees]);
    }
    for (const [etape, refus] of refusees) sautees.push({ etape, refus });
    if (nonProuvees.size === 0) return { aEcrire: etapes.filter((etape) => !refusees.has(etape)), etapes, lots, ecartes, sautees };
    if (tour === TOURS_DE_PREUVE_MAX) return { nonProuvees: [...nonProuvees] };
    for (const { paragraphe, plage } of nonProuvees) if (plage !== undefined) ecartes.push({ paragraphe, plage, raison: 'position_not_proven' });
    etapes = etapes.filter((etape) => !nonProuvees.has(etape) && !refusees.has(etape));
  }
}

/**
 * Le refus à rendre quand {@link prouverLePlan} laisse des étapes non prouvées
 * au dernier tour : rien n'est écrit. Il redit ce qui était visé — le texte
 * exact, le filtre, ou les deux — et nomme les paragraphes à écarter de la cible.
 *
 * @param origine D'où viennent les passages visés. `undefined` : l'écriture ne
 *   vise pas de passages par position, donc aucune étape ne reste non prouvée ;
 *   le message compte alors des passages sans les qualifier.
 */
export function refusDesPositionsNonProuvees(
  nonProuvees: readonly EtapeAEcrire[],
  origine: OrigineDeLaSelection | undefined,
): { code: 'FORMAT_WRITE_REFUSED'; message: string } {
  const paragraphes = [...new Set(nonProuvees.map((etape) => etape.paragraphe.nodeId))];
  const vises = origine === undefined ? `${nonProuvees.length} passage(s)` : direLesPassagesVises(origine, nonProuvees.length);
  return {
    code: 'FORMAT_WRITE_REFUSED',
    message: `format_text was refused before any write: after ${TOURS_DE_PREUVE_MAX} previews, the editor still does not place ${vises} `
      + 'where the document file places them, in paragraph(s) '
      + `${paragraphes.slice(0, PARAGRAPHES_CITES_MAX).join(', ')}${paragraphes.length > PARAGRAPHES_CITES_MAX ? ` and ${paragraphes.length - PARAGRAPHES_CITES_MAX} more` : ''}. `
      + 'Nothing was written. Resend the action on a target that leaves these paragraphs out, and tell the lawyer they were left as is.',
  };
}
